/**
 * 原文ビューア（FR-27・05 §6 C-03 左ペイン）＝**AI が何を見て下書きしたか**を人が原文で確かめる場所。
 *
 * 07 §7 の核＝**原文はエラーで消えない**。AI が読めなくても FAX 画像・メール本文はそのまま見える。
 * 3バリアント（design-brief-ai-inbox §3）：
 *   1. 画像（FAX スキャン・写真）＝署名URLで表示＋ズーム（かすれ・手書きを人が読む）
 *   2. メール＝差出/件名相当は raw_text 本文、添付はサムネイル→クリックで拡大
 *   3. 電話メモ／アプリ＝構造化（プレーンテキスト or カタログ選択）
 *
 * ★署名URLは15分で失効するので**都度取得**（都度 useQuery）。画像は原文＝**編集不可・参照専用**。
 */

import { Card, ErrorNote, Spinner } from "@kenzai/ui";
import { console_, type AttachmentRef, type OrderDetail as Detail } from "@kenzai/api-client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

const IMAGE_KINDS = new Set(["fax_image", "photo"]);

export function SourceViewer({ detail }: { detail: Detail }) {
  const { order, raw_text, attachments } = detail;
  const images = attachments.filter((a) => IMAGE_KINDS.has(a.kind));
  const mailAttachments = attachments.filter((a) => a.kind === "mail_attach");

  // 変種の判定はチャネル名でなく**手元に何があるか**で決める（FAX の AI 起票も、電話の手入力も
  // 同じ入口を通るため＝07 §1）。画像があれば画像・無くて本文があればテキスト・どちらも無ければ構造化。
  let body;
  if (images.length > 0) {
    body = <ImageSource images={images} rawText={raw_text} />;
  } else if (raw_text) {
    body = <TextSource channel={order.channel} rawText={raw_text} mailAttachments={mailAttachments} />;
  } else {
    body = <StructuredSource detail={detail} />;
  }

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--kz-space-2)", marginBottom: "var(--kz-space-3)" }}>
        <h2 style={{ margin: 0, fontSize: "var(--kz-font-size-md)" }}>取引先が送った内容</h2>
        <span style={{ fontSize: 11, color: "var(--kz-color-ink-muted)" }}>原文（参照専用）</span>
      </div>
      {body}
    </Card>
  );
}

/** 画像バリアント（FAX スキャン・写真）＝署名URLで表示・クリックでズーム。 */
function ImageSource({ images, rawText }: { images: AttachmentRef[]; rawText: string | null }) {
  return (
    <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>
      {rawText && (
        // メール＋画像添付のように本文もあるときは本文を先に（差出人の意図が読める）
        <pre style={mailBodyStyle}>{rawText}</pre>
      )}
      {images.map((a) => (
        <AttachmentImage key={a.id} attachment={a} />
      ))}
    </div>
  );
}

/** テキストバリアント（メール本文・電話メモ）＝原文の文字。メール添付はサムネイル。 */
function TextSource({
  channel,
  rawText,
  mailAttachments,
}: {
  channel: string;
  rawText: string;
  mailAttachments: AttachmentRef[];
}) {
  const label = channel === "tel" ? "電話で聞き取った内容" : channel === "mail" ? "受信メール本文" : "受信内容";
  return (
    <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>
      <div style={{ fontSize: 11, color: "var(--kz-color-ink-muted)" }}>{label}</div>
      <pre style={mailBodyStyle}>{rawText}</pre>
      {mailAttachments.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: "var(--kz-color-ink-muted)", marginBottom: "var(--kz-space-1)" }}>
            添付
          </div>
          <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
            {mailAttachments.map((a) => (
              <AttachmentImage key={a.id} attachment={a} thumb />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** 構造化バリアント（アプリのカタログ注文・電話の手入力）＝原文がテキストでない＝明細そのもの。 */
function StructuredSource({ detail }: { detail: Detail }) {
  const { order } = detail;
  const note = order.channel === "web" ? "アプリのカタログから選ばれた注文です（品名・数量は取引先が直接選択）。" : "電話・店頭で受けた注文です（担当者が内容を入力）。";
  return (
    <div style={{ display: "grid", gap: "var(--kz-space-2)", fontSize: "var(--kz-font-size-sm)" }}>
      <p style={{ margin: 0, color: "var(--kz-color-ink-muted)" }}>{note}</p>
      {detail.items.map((i) => (
        <div
          key={i.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "var(--kz-space-2)",
            background: "var(--kz-color-surface-sunken)",
            borderRadius: "var(--kz-radius-sm)",
          }}
        >
          <span>{i.raw_text ?? `商品 #${i.product_id}`}</span>
          <span className="kz-num">
            {i.qty === null ? (
              // FR-26：読み取れない数量は推測しない＝0 と捏造せず正直に（Draft 表と同じ扱い）
              <span style={{ color: "var(--kz-status-warn)" }}>読み取れません</span>
            ) : (
              <>
                {Number(i.qty)} {i.unit}
              </>
            )}
          </span>
        </div>
      ))}
      {order.note && (
        <div>
          <div style={{ fontWeight: 600, marginTop: "var(--kz-space-2)" }}>備考</div>
          <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{order.note}</p>
        </div>
      )}
    </div>
  );
}

const mailBodyStyle = {
  margin: 0,
  padding: "var(--kz-space-3)",
  background: "var(--kz-color-surface-sunken)",
  borderRadius: "var(--kz-radius-sm)",
  fontSize: "var(--kz-font-size-sm)",
  fontFamily: "var(--kz-font-sans)",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 480,
  overflow: "auto",
} as const;

/** 1添付＝署名URLを都度取得して画像 or PDF を出す。クリックでズーム（Lightbox）。 */
function AttachmentImage({ attachment, thumb = false }: { attachment: AttachmentRef; thumb?: boolean }) {
  // 署名URLは15分で失効。ページを開いたまま放置しても切れないよう、寿命の手前（12分）で
  // 自動的に取り直す＝インライン画像・PDF の src が常に有効なURLに保たれる（FR-27）。
  const q = useQuery({
    queryKey: ["attachment-url", attachment.id],
    queryFn: () => console_.attachmentUrl(attachment.id),
    staleTime: 10 * 60 * 1000,
    refetchInterval: 12 * 60 * 1000,
  });
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const isPdf = attachment.content_type === "application/pdf";

  if (q.isLoading) return <Spinner label="原文を読み込み中…" />;
  if (q.isError || !q.data) return <ErrorNote error={q.error ?? new Error("原文を表示できません。")} />;
  const url = q.data.url;

  if (isPdf) {
    // PDF はブラウザ内蔵ビューアで（object）＝FAX 複合機の PDF スキャンをそのまま読む
    return (
      <div>
        <object data={url} type="application/pdf" style={{ width: "100%", height: 520, border: "1px solid var(--kz-color-border)", borderRadius: "var(--kz-radius-sm)" }} aria-label="FAX 原文（PDF）">
          <a href={url} target="_blank" rel="noreferrer" style={{ color: "var(--kz-color-primary)" }}>
            PDF を別タブで開く
          </a>
        </object>
      </div>
    );
  }

  if (broken) {
    // dev のスタブストアは実体を配信しないので画像が出ないことがある＝原文へのリンクは残す
    return (
      <a href={url} target="_blank" rel="noreferrer" style={{ color: "var(--kz-color-primary)", fontSize: "var(--kz-font-size-sm)" }}>
        原文（画像）を別タブで開く
      </a>
    );
  }

  const imgStyle = thumb
    ? { width: 96, height: 96, objectFit: "cover" as const, borderRadius: "var(--kz-radius-sm)", border: "1px solid var(--kz-color-border)" }
    : { width: "100%", maxHeight: 520, objectFit: "contain" as const, borderRadius: "var(--kz-radius-sm)", border: "1px solid var(--kz-color-border)", background: "var(--kz-color-surface-sunken)" };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="クリックで拡大"
        data-testid={`attachment-${attachment.id}`}
        style={{ padding: 0, border: "none", background: "none", cursor: "zoom-in", display: "block", width: thumb ? "auto" : "100%" }}
      >
        <img src={url} alt="注文の原文" onError={() => setBroken(true)} style={imgStyle} />
      </button>
      {open && <Lightbox attachment={attachment} onClose={() => setOpen(false)} />}
    </>
  );
}

/** 原文画像の拡大（ズーム/パン）。倍率はボタン・パンはスクロール・Esc/外側クリックで閉じる。
 *  ★開いた時点で**署名URLを取り直す**（インラインの取得から15分経っていても、ズームは常に有効なURL）。
 *  取得失敗・画像エラーでも「別タブで開く」に落として**手詰まりにしない**（原文は消えない・FR-27 §7）。 */
function Lightbox({ attachment, onClose }: { attachment: AttachmentRef; onClose: () => void }) {
  const [zoom, setZoom] = useState(1);
  const [broken, setBroken] = useState(false);
  const q = useQuery({
    queryKey: ["attachment-url-zoom", attachment.id],
    queryFn: () => console_.attachmentUrl(attachment.id),
    staleTime: 0, // 開くたびに新しい署名URL＝失効した古いURLでズームしない
    gcTime: 0,
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const url = q.data?.url;
  const canZoom = Boolean(url) && !broken;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="原文の拡大"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, background: "var(--kz-color-scrim-strong)", zIndex: 60, display: "grid", gridTemplateRows: "auto 1fr", padding: "var(--kz-space-3)" }}
    >
      <div style={{ display: "flex", gap: "var(--kz-space-2)", alignItems: "center", padding: "var(--kz-space-2)" }}>
        <button type="button" disabled={!canZoom} onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} style={lightboxBtn} aria-label="縮小">
          −
        </button>
        <span className="kz-num" style={{ color: "var(--kz-color-ink-inverse)", minWidth: 56, textAlign: "center" }}>
          {Math.round(zoom * 100)}%
        </span>
        <button type="button" disabled={!canZoom} onClick={() => setZoom((z) => Math.min(4, z + 0.25))} style={lightboxBtn} aria-label="拡大">
          ＋
        </button>
        <button type="button" disabled={!canZoom} onClick={() => setZoom(1)} style={lightboxBtn}>
          等倍
        </button>
        <button type="button" onClick={onClose} data-testid="lightbox-close" style={{ ...lightboxBtn, marginLeft: "auto" }}>
          閉じる
        </button>
      </div>
      <div style={{ overflow: "auto", display: "grid", placeItems: zoom <= 1 ? "center" : "start" }}>
        {q.isLoading ? (
          <Spinner label="原文を読み込み中…" />
        ) : !url ? (
          <ErrorNote error={q.error ?? new Error("原文を表示できません。")} />
        ) : broken ? (
          // 署名URLが失効/配信不可でも別タブで開けるようにする（dev のスタブストア含む）
          <a href={url} target="_blank" rel="noreferrer" data-testid="lightbox-open-tab" style={{ color: "var(--kz-color-ink-inverse)", fontWeight: 600 }}>
            原文（画像）を別タブで開く
          </a>
        ) : (
          <img
            src={url}
            alt="注文の原文（拡大）"
            onError={() => setBroken(true)}
            style={{ transform: `scale(${zoom})`, transformOrigin: "top left", maxWidth: zoom <= 1 ? "100%" : "none", maxHeight: zoom <= 1 ? "100%" : "none" }}
          />
        )}
      </div>
    </div>
  );
}

const lightboxBtn = {
  minHeight: 40,
  minWidth: 40,
  padding: "0 var(--kz-space-3)",
  border: "1px solid rgba(255,255,255,0.4)",
  borderRadius: "var(--kz-radius-sm)",
  background: "rgba(255,255,255,0.12)",
  color: "var(--kz-color-ink-inverse)",
  font: "inherit",
  fontWeight: 600,
  cursor: "pointer",
} as const;
